import React from "react";
import { API_URLS } from "../../configs/api.urls";
import { parseSignature } from "./InspectionReport";
import logo from "./../../assets/images/logo.png";

import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Font,
  Svg,
  Path,
} from "@react-pdf/renderer";

const GenerateInspectionReport = ({ report }) => {
  const options = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true,
    timeZone: "Asia/Dubai",
  };

  Font.register({
    family: "Poppins",
    fonts: [
      {
        src: "/fonts/Poppins-Regular.ttf",
        fontWeight: "normal",
      },
      {
        src: "/fonts/Poppins-SemiBold.ttf",
        fontWeight: "semibold",
      },
    ],
  });

  const parseFieldValue = (field, isPrev = false) => {
    let value;
    if (field.type === "checkbox") {
      field.data
        ? field.data === "on" || true
          ? (value = (
              <Svg
                xmlns="http://www.w3.org/2000/svg"
                fill={isPrev ? "#3f3f4690" : "#16a34a"}
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                style={styles.icon}
              >
                <Path
                  fillRule="evenodd"
                  d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                  clipRule="evenodd"
                />
              </Svg>
            ))
          : (value = (
              <Svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill={isPrev ? "#3f3f4690" : "#b91c1c"}
                style={styles.icon}
              >
                <Path
                  fillRule="evenodd"
                  d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                  clipRule="evenodd"
                />
              </Svg>
            ))
        : (value = (
            <Svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill={isPrev ? "#3f3f4690" : "#b91c1c"}
              style={styles.icon}
            >
              <Path
                fillRule="evenodd"
                d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                clipRule="evenodd"
              />
            </Svg>
          ));
    } else {
      value = <Text style={styles.textXs}>{field.data ?? "--"}</Text>;
    }
    return value;
  };

  const returnOutlines = (outline) => {
    if (outline._id) {
      delete outline?._id;
    }
    return outline;
  };

  const validateAssetURL = (url) => {
    if (url == null || "") {
      return "https://img.icons8.com/parakeet-line/48/remove-image.png";
    } else {
      if (url.startsWith("/")) {
        return `${API_URLS.ASSETS_BACKEND_URL}${url}`;
      }
      return `${API_URLS.ASSETS_BACKEND_URL}/${url}`;
    }
  };

  return (
    <Document
      creator={"www.mylocatorplus.com"}
      producer={"www.mylocatorplus.com"}
      title={`Vehicle Inspection Report - ${report?.vehicle?.vehicleName}`}
    >
      <Page size="A4" style={{ height: "100%", backgroundColor: "white" }}>
        <View style={[styles.bgWhite, styles.w100, styles.fontPoppins]}>
          <View
            style={[
              styles.dFlex,
              styles.flexRow,
              styles.bgPrimary,
              styles.px5,
              styles.py4,
              styles.textWhite,
              styles.justifyBetween,
            ]}
          >
            <View
              style={[
                styles.w100,
                styles.dFlex,
                styles.column,
                styles.justifyCenter,
              ]}
            >
              <Text style={[styles.h4, styles.textWhite]}>
                Vehicle Inspection Report
              </Text>
              <Text
                style={[styles.pb0, styles.h6, styles.mb1, styles.textWhite]}
              >
                {report?.vehicle?.vehicleName ?? "Vehicle Name"}
              </Text>
              <Text style={[styles.mb0, styles.textUpper, styles.h6]}>
                {Intl.DateTimeFormat("en-GB", options).format(
                  Date.parse(report?.createdAt)
                )}
              </Text>
            </View>
            <View style={[styles.dFlex, styles.column, styles.justifyCenter]}>
              <Image style={{ width: 100 }} src={logo} />
            </View>
          </View>

          {/* Main Section */}
          {report?.mandatory.length && (
            <View style={[styles.py5, styles.px5]}>
              {/* Section Header */}
              <View>
                <Text
                  style={[
                    styles.my2,
                    styles.px2,
                    styles.h6,
                    styles.py1,
                    styles.bgSecondary,
                    styles.textWhite,
                  ]}
                >
                  MANDATORY FIELDS
                </Text>

                {/* Grid Replacement */}
                <View
                  style={[
                    styles.grid2Col,
                    {
                      backgroundColor: "#333",
                      gap: "0.5pt",
                      padding: "0.5pt",
                    },
                  ]}
                >
                  {report?.mandatory?.map((field, key) => (
                    <View
                      key={key}
                      style={[
                        styles.dFlex,
                        styles.flexRow,
                        styles.bgWhite,
                        styles.p2,
                        styles.justifyBetween,
                        styles.alignCenter,
                        styles.gridItemHalf,
                      ]}
                    >
                      <Text style={[styles.textMd, styles.mb0]}>
                        {field?.label ?? "label"}
                      </Text>
                      {/* <Text style={styles.textXs}> */}
                      {parseFieldValue(field)}
                      {/* </Text> */}
                    </View>
                  ))}
                  {report?.mandatory?.length % 2 !== 0 && (
                    <View
                      style={[
                        styles.dFlex,
                        styles.bgWhite,
                        styles.p2,
                        styles.justifyBetween,
                        styles.alignCenter,
                        styles.gridItemHalf,
                      ]}
                    >
                      <Text style={[styles.textMd, styles.mb0]}></Text>
                      <Text></Text>
                    </View>
                  )}
                </View>
              </View>
            </View>
          )}
          {report?.optional?.length && (
            <View style={[styles.py5, styles.px5]}>
              {/* Section Header */}
              <View>
                <Text
                  style={[
                    styles.my2,
                    styles.px2,
                    styles.h6,
                    styles.py1,
                    styles.bgSecondary,
                    styles.textWhite,
                  ]}
                >
                  OPTIONAL FIELDS
                </Text>

                {/* Grid Replacement */}
                <View
                  style={[
                    styles.grid2Col,
                    {
                      backgroundColor: "#333",
                      gap: 0.5,
                      padding: 0.5,
                    },
                  ]}
                >
                  {report?.optional.map((field, key) => (
                    <View
                      key={key}
                      style={[
                        styles.dFlex,
                        styles.flexRow,
                        styles.bgWhite,
                        styles.p2,
                        styles.justifyBetween,
                        styles.alignCenter,
                        styles.gridItemHalf,
                      ]}
                    >
                      <Text style={[styles.textMd, styles.mb0]}>
                        {field?.label ?? "label"}
                      </Text>
                      {parseFieldValue(field) ?? "field data"}
                    </View>
                  ))}
                  {report?.optional?.length % 2 !== 0 && (
                    <View
                      style={[
                        styles.dFlex,
                        styles.bgWhite,
                        styles.p2,
                        styles.justifyBetween,
                        styles.alignCenter,
                        styles.gridItemHalf,
                      ]}
                    >
                      <Text style={[styles.textMd, styles.mb0]}></Text>
                      <Text></Text>
                    </View>
                  )}
                </View>
              </View>
            </View>
          )}
          {/* {report?.marks?.length && ( */}
          <View style={[styles.py5, styles.px5]}>
            {/* Section Header */}
            <View>
              <Text
                style={[
                  styles.my2,
                  styles.px2,
                  styles.h6,
                  styles.py1,
                  styles.bgSecondary,
                  styles.textWhite,
                ]}
              >
                SCRATCHES AND OTHER MARKS
              </Text>
              <View
                style={[
                  styles.my4,
                  {
                    display: "flex",
                    flexWrap: "nowrap",
                    flexDirection: "row",
                  },
                ]}
              >
                {Object.values(returnOutlines(report.outlines)).map(
                  (i, index) => (
                    <Image
                      key={index}
                      style={[styles.grid5Item, { width: "20%" }]}
                      src={validateAssetURL(i)}
                    />
                  )
                )}
              </View>

              {Object.entries(returnOutlines(report.outlines)).map(
                ([key, value]) => {
                  if (value.startsWith("images/inspection/uploaded-photos/")) {
                    return (
                      <View key={key} style={[styles.w100, styles.mb6]}>
                        <View wrap={false}>
                          <Text
                            style={[
                              styles.my2,
                              styles.mx4,
                              styles.px2,
                              styles.h6,
                              styles.py1,
                              styles.bgSecondary50,
                              styles.textWhite,
                              { textTransform: "uppercase" },
                            ]}
                          >
                            {key ?? "key"}
                          </Text>
                          <View style={[styles.justifyCenter, styles.dFlex]}>
                            <Image
                              src={validateAssetURL(value)}
                              style={[
                                styles.w50,
                                { marginLeft: "auto", marginRight: "auto" },
                              ]}
                            />
                          </View>
                        </View>
                        <View style={styles.my4}>
                          {report.marks
                            .filter((mark) => mark.side === key)
                            .map((mark, index) => (
                              <View
                                key={index}
                                style={[
                                  styles.grid2Col,
                                  {
                                    backgroundColor: "#333",
                                    gap: 0.5,
                                    padding: 0.5,
                                  },
                                ]}
                              >
                                <Image
                                  src={validateAssetURL(mark.imagePath)}
                                  style={[
                                    styles.p2,
                                    styles.gridItemThird,
                                    styles.bgWhite,
                                    {
                                      width: "200px",
                                      maxHeight: 300,

                                      objectFit: "contain",
                                      // transform: "rotate(90)",
                                    },
                                  ]}
                                />

                                <View
                                  style={[
                                    styles.gridItemTwoThird,
                                    styles.bgWhite,
                                    { flex: 1 },
                                  ]}
                                >
                                  <View>
                                    <Text
                                      style={[
                                        styles.headingSmall,
                                        styles.p1,
                                        styles.bgSecondary25,
                                        styles.textCapitalize,
                                      ]}
                                    >
                                      TYPE: {mark?.markType ?? "mark Type"}
                                    </Text>
                                  </View>
                                  <View style={[styles.p2]}>
                                    <Text
                                      style={[
                                        styles.textMd,
                                        mark.description == (null || "")
                                          ? styles.textGray
                                          : null,
                                      ]}
                                    >
                                      {mark.description ?? " No Description"}
                                    </Text>
                                  </View>
                                </View>
                              </View>
                            ))}
                        </View>
                      </View>
                    );
                  }
                }
              )}
            </View>
          </View>
          {/* )} */}
          {report?.photos?.length && (
            <View style={[styles.py5, styles.px5]}>
              {/* Section Header */}
              <View>
                <Text
                  style={[
                    styles.my2,
                    styles.px2,
                    styles.h6,
                    styles.py1,
                    styles.bgSecondary,
                    styles.textWhite,
                  ]}
                >
                  PHOTOS
                </Text>

                <View
                  style={[
                    styles.my4,
                    {
                      display: "flex",
                      flexWrap: "wrap",
                      flexDirection: "row",
                    },
                  ]}
                >
                  {report.photos.map((photo, idx) => (
                    <Image
                      key={idx}
                      src={validateAssetURL(photo)}
                      style={[
                        {
                          width: "40vw",
                          maxHeight: "40vh",
                          objectFit: "contain",
                          marginHorizontal: "1%",
                          marginVertical: "2%",
                        },
                      ]}
                    />
                  ))}
                </View>
              </View>
            </View>
          )}
          {report?.remarks?.length && (
            <View style={[styles.py5, styles.px5]}>
              {/* Section Header */}
              <View>
                <Text
                  style={[
                    styles.my2,
                    styles.px2,
                    styles.h6,
                    styles.py1,
                    styles.bgSecondary,
                    styles.textWhite,
                  ]}
                >
                  REMARKS
                </Text>
                <View style={styles.border}>
                  <Text
                    style={[
                      // styles.textUppercase,
                      styles.mb0,
                      styles.p2,
                      styles.textMd,
                    ]}
                  >
                    {report?.remarks ?? "No remarks"}
                  </Text>
                </View>
              </View>
            </View>
          )}

          <View style={[styles.py5, styles.px5, styles.mb2]}>
            {/* Section Header */}
            <View>
              <Text
                style={[
                  styles.my2,
                  styles.px2,
                  styles.h6,
                  styles.py1,
                  styles.bgSecondary,
                  styles.textWhite,
                ]}
              >
                ACKNOWLEDGEMENTS
              </Text>
              <View style={styles.border}>
                {/* Driver */}
                <View
                  style={[
                    styles.w100,
                    styles.dFlex,
                    styles.flexRow,
                    styles.alignCenter,
                  ]}
                >
                  <Text
                    style={[
                      styles.textUppercase,
                      styles.w50,
                      styles.mb0,
                      styles.p2,
                      ,
                      styles.textMd,
                    ]}
                  >
                    DRIVER NAME: {report.driverSignature?.name ?? "--"}
                  </Text>
                  <View
                    style={[
                      styles.w50,
                      styles.dFlex,
                      styles.alignCenter,
                      styles.flexRow,
                      styles.dFlex,
                    ]}
                  >
                    <Text
                      style={[styles.textUppercase, styles.mb0, styles.textMd]}
                    >
                      SIGNATURE:
                    </Text>
                    {report?.driverSignature?.signature?.length && (
                      <Image
                        src={parseSignature(
                          report?.driverSignature?.signature ?? ""
                        )}
                        style={{ height: "50px" }}
                      />
                    )}
                  </View>
                </View>

                {/* Inspector */}
                <View
                  style={[
                    styles.w100,
                    styles.dFlex,
                    styles.flexRow,
                    styles.alignCenter,
                  ]}
                >
                  <Text
                    style={[
                      styles.textUppercase,
                      styles.w50,
                      styles.mb0,
                      styles.p2,
                      ,
                      styles.textMd,
                    ]}
                  >
                    INSPECTOR NAME: {report.inspectorSignature?.name ?? "--"}
                  </Text>
                  <View
                    style={[
                      styles.w50,
                      styles.alignCenter,
                      styles.flexRow,
                      styles.dFlex,
                    ]}
                  >
                    <Text
                      style={[styles.textUppercase, styles.mb0, styles.textMd]}
                    >
                      SIGNATURE:
                    </Text>
                    {report?.inspectorSignature?.signature?.length && (
                      <Image
                        src={parseSignature(
                          report?.inspectorSignature?.signature ?? ""
                        )}
                        style={{ height: "50px" }}
                      />
                    )}
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default GenerateInspectionReport;

export const styles = StyleSheet.create({
  fontPoppins: { fontFamily: "Poppins" },
  // General utility
  w100: { width: "100%" },
  w50: { width: "50%" },
  h100: { height: "100%" },
  p0: { padding: 0 },
  p1: { padding: 4 },
  p2: { padding: 8 },
  p4: { padding: 16 },
  p5: { padding: 40 },
  pt5: { paddingTop: 40 },
  px2: { paddingHorizontal: 8 },
  px5: { paddingHorizontal: 40 },
  py1: { paddingVertical: 4 },
  py4: { paddingVertical: 16 },
  py5: { paddingVertical: 20 },
  mx2: { marginHorizontal: 8 },
  mx4: { marginHorizontal: 16 },
  my1: { marginVertical: 4 },
  m2: { margin: 8 },
  my2: { marginVertical: 8 },
  my4: { marginVertical: 16 },
  mb0: { marginBottom: 0 },
  mb1: { marginBottom: 4 },
  mb2: { marginBottom: 8 },
  mb4: { marginBottom: 16 },
  mb6: { marginBottom: 24 },
  mt4: { marginTop: 16 },

  // Backgrounds & Borders
  bgWhite: { backgroundColor: "#ffffff" },
  bgPrimary: { backgroundColor: "#007bff" },
  bgSecondary: { backgroundColor: "#6c757d" },
  bgSecondary50: { backgroundColor: "#6c757daa" },
  bgSecondary25: { backgroundColor: "#6c757d25" },
  bgDark: { backgroundColor: "#343a40" },
  border: { border: "0.5pt solid #000" },

  // Text
  textWhite: { color: "#ffffff" },
  textSecondary: { color: "#6c757d" },
  textUppercase: { textTransform: "uppercase" },
  textCapitalize: { textTransform: "capitalize" },
  headingSmall: { fontSize: 10, fontWeight: "semibold" },
  h4: { fontSize: 16, fontWeight: "semibold" },
  h5: { fontSize: 14, fontWeight: "semibold" },
  h6: { fontSize: 10, fontWeight: "semibold" },
  textXs: { fontSize: 9 },
  textMd: { fontSize: 10 },
  icon: { height: "20px", width: "20px" },

  // Flex/grid layout
  dFlex: { display: "flex" },
  flexColumn: { flexDirection: "column" },
  flexRow: { flexDirection: "row" },
  justifyBetween: { justifyContent: "space-between" },
  justifyCenter: { justifyContent: "center" },
  alignCenter: { alignItems: "center" },
  placeContentCenter: {
    justifyContent: "center",
    alignItems: "center",
  },

  // Grid - emulate grid layout manually (custom solution)
  grid2Col: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    // maxwidth: "100%",
  },
  gridItemHalf: {
    width: "49.9%",
  },
  grid5Col: {
    display: "flex",
    flexWrap: "nowrap",
    flexDirection: "row",
  },
  gridItemFifth: {
    width: "20%",
  },
  gridItemThird: {
    width: "32%",
  },
  gridItemTwoThird: {
    width: "65%",
  },

  // Image
  logoImage: {
    width: 150,
  },
  scratchImage: {
    objectFit: "contain",
    width: 200,
  },
  fullWidthImage: {
    width: "100%",
  },

  // Signature image
  signatureImage: {
    height: 100,
  },

  // Custom section label
  sectionLabel: {
    padding: 4,
    fontSize: 12,
    fontWeight: "semibold",
    color: "#fff",
    backgroundColor: "#007bff",
    textTransform: "uppercase",
  },

  // Custom layout
  section: {
    marginVertical: 16,
  },

  // Table-like layout (mimic bootstrap Table)
  tableRow: {
    display: "flex",
    flexDirection: "row",
  },
  tableCell: {
    border: "1pt solid #000",
    padding: 4,
    fontSize: 10,
    flex: 1,
  },
  tableCellImage: {
    width: 200,
    objectFit: "contain",
  },
  tableCellHalf: {
    flex: 1,
  },
  table: {
    display: "table",
    width: "100%",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#000",
    marginBottom: 10,
  },
  row: {
    flexDirection: "row",
  },
  cell: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    padding: 4,
    fontSize: 10,
  },
  imageCell: {
    width: 200,
    padding: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  image: {
    width: 200,
    objectFit: "contain",
  },
  textCapitalize: {
    textTransform: "capitalize",
    fontWeight: "semibold",
    marginBottom: 0,
  },
  textGray: {
    // fontStyle: "italic",
    color: "gray",
  },
});
